import { useState, type JSX } from 'react';

import { shaktiApi, ApiError } from '../api/client';
import { useProjectScopedEffect } from '../stores/project';

/**
 * M4.1 (round-5) + #121 (round-6 close-out) — GDPR taint-tracking viewer.
 *
 * Round-5 shipped a hardcoded summary; #121 replaces the constant
 * with a live fetch of `/v1/taint/summary`. The endpoint aggregates
 * `audit_entries`, `users`, `provider_configs`, and `vcs_credentials`
 * rows scoped to the active org so a compliance reviewer sees real
 * counts. Enforcement (block a deploy when an uncleared taint
 * crosses) is still v2.1; this page documents the current boundary.
 */
interface TaintBucket {
  tag: string;
  description: string;
  usage_count: number;
  example_paths: string[];
}

/**
 * Static reference legend — describes what each tag means. The live
 * counts come from the `/v1/taint/summary` endpoint and are merged
 * against this table so the page always shows a stable set of rows
 * with descriptions even when a new tag appears or drops to zero.
 */
const TAINT_LEGEND: TaintBucket[] = [
  {
    tag: 'provider_keys',
    description: 'BYOK LLM provider API keys (encrypted with SHAKTI_PROVIDER_KEK)',
    usage_count: 0,
    example_paths: ['crates/shakti-db/src/repos/provider_config.rs:api_key_encrypted'],
  },
  {
    tag: 'vcs_credentials',
    description: 'Per-project GitHub / GitLab PATs (encrypted with SHAKTI_VCS_KEK)',
    usage_count: 0,
    example_paths: ['crates/shakti-db/src/repos/vcs_credentials.rs:pat_encrypted'],
  },
  {
    tag: 'audit_entries',
    description: 'Merkle-chained audit entries — scoped per org (V00019 RLS)',
    usage_count: 0,
    example_paths: ['crates/shakti-db/src/repos/audit.rs:AuditEntryRow'],
  },
  {
    tag: 'users',
    description: 'Natural-person identifiers (GDPR Article 4 personal data)',
    usage_count: 0,
    example_paths: ['crates/shakti-db/src/repos/user.rs:UserRow'],
  },
];

export function DataResidencyRoute(): JSX.Element {
  const [summary, setSummary] = useState<{
    totals: { tracked_resources: number; dsar_requests_last_90d: number; audit_entries: number };
    by_kind: Array<{ kind: string; count: number }>;
    generated_at: string;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useProjectScopedEffect((signal) => {
    shaktiApi
      .getTaintSummary({ signal })
      .then(setSummary)
      .catch((e: unknown) => {
        if (signal.aborted) return;
        setError(
          e instanceof ApiError ? e.body?.error ?? e.message : 'failed to load taint summary',
        );
      });
  });

  const byKind: Record<string, number> = {};
  for (const row of summary?.by_kind ?? []) {
    byKind[row.kind] = row.count;
  }
  const merged: TaintBucket[] = TAINT_LEGEND.map((row) => ({
    ...row,
    usage_count: byKind[row.tag] ?? row.usage_count,
  }));

  return (
    <section aria-labelledby="dr-title" className="mx-auto flex max-w-5xl flex-col gap-6">
      <header>
        <span className="shakti-mono text-2xs uppercase tracking-wider text-fg-muted">
          /dashboard/data-residency
        </span>
        <h1 id="dr-title" className="text-2xl font-semibold text-fg-primary">
          Data residency & taint map
        </h1>
        <p className="text-sm text-fg-secondary">
          Live view of the tags Shakti's taint engine propagates through your org's resources.
          Counts are fetched from <code>/v1/taint/summary</code> and scoped to the authenticated
          organisation via V00019 RLS. Enforcement (block deploys when a taint crosses into an
          uncleared region) remains v2.1 on the roadmap.
        </p>
      </header>

      {error && (
        <div role="alert" className="shakti-card border-status-error text-status-error">
          {error}
        </div>
      )}

      {summary && (
        <section aria-label="Compliance totals" className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div className="shakti-card flex flex-col gap-1">
            <span className="text-2xs uppercase tracking-wider text-fg-muted">Tracked resources</span>
            <span className="text-2xl font-semibold text-fg-primary">
              {summary.totals.tracked_resources}
            </span>
          </div>
          <div className="shakti-card flex flex-col gap-1">
            <span className="text-2xs uppercase tracking-wider text-fg-muted">DSAR requests · 90d</span>
            <span className="text-2xl font-semibold text-fg-primary">
              {summary.totals.dsar_requests_last_90d}
            </span>
          </div>
          <div className="shakti-card flex flex-col gap-1">
            <span className="text-2xs uppercase tracking-wider text-fg-muted">Audit entries</span>
            <span className="text-2xl font-semibold text-fg-primary">
              {summary.totals.audit_entries}
            </span>
          </div>
        </section>
      )}

      <section className="shakti-card p-6" aria-label="Taint legend">
        <h2 className="text-lg font-semibold">What is a taint tag?</h2>
        <p className="mt-2 text-sm text-fg-secondary">
          A taint tag is a classification that travels with a value through the code graph.
          A field marked <code>provider_keys</code> in one repo propagates to every call site
          that reads or copies it. At deploy time the policy engine verifies no tagged value
          ever crosses a boundary the customer hasn't cleared for that tag.
        </p>
      </section>

      <section className="shakti-card p-0" aria-label="Taint summary">
        <table className="w-full text-sm">
          <thead className="bg-bg-raised">
            <tr className="text-left text-2xs uppercase tracking-wider text-fg-muted">
              <th className="px-4 py-3">Tag</th>
              <th className="px-4 py-3">Description</th>
              <th className="px-4 py-3 text-right">Resources</th>
              <th className="px-4 py-3">Example path</th>
            </tr>
          </thead>
          <tbody>
            {merged.map((b) => (
              <tr key={b.tag} className="border-t border-border-subtle">
                <td className="px-4 py-3 shakti-mono">{b.tag}</td>
                <td className="px-4 py-3 text-fg-secondary">{b.description}</td>
                <td className="px-4 py-3 text-right font-semibold">{b.usage_count}</td>
                <td className="px-4 py-3 shakti-mono text-2xs text-fg-muted">
                  {b.example_paths[0] ?? '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section className="shakti-card p-6">
        <h2 className="text-lg font-semibold">What ships in v2.1</h2>
        <ul className="mt-3 space-y-2 text-sm text-fg-secondary">
          <li>• Live taint graph with cross-call-site propagation</li>
          <li>• Policy editor for the allowed boundary crossings per tag</li>
          <li>• Pre-deploy gate that blocks when an uncleared crossing is detected</li>
          <li>
            • DSAR automation is available today via Settings → Compliance; v2.1 adds
            policy-driven enforcement.
          </li>
        </ul>
      </section>
    </section>
  );
}
